import { getPromotionAnalytics } from '../SingletonAnalyticsManager';

/**
 * VDP Promotion Tracker utility
 * Call this on vehicle detail pages to track promotion-related visits
 */
export default class VdpPromotionTracker {
  constructor() {
    this.promotionAnalytics = null;
    this.isInitialized = false;
  }

  /**
   * Initialize the VDP promotion tracker
   * @returns {Promise<boolean>}
   */
  async initialize() {
    try {
      this.promotionAnalytics = await getPromotionAnalytics();
      this.isInitialized = this.promotionAnalytics.isReady();
      return this.isInitialized;
    } catch (error) {
      console.warn('Failed to initialize VdpPromotionTracker:', error);
      return false;
    }
  }

  /**
   * Track VDP visit with vehicle data
   * @param {Object} vehicleData - Vehicle information
   * @param {string} vehicleData.stock_id - Vehicle stock ID
   * @param {string} vehicleData.make - Vehicle make
   * @param {string} vehicleData.model - Vehicle model
   * @param {string} vehicleData.year - Vehicle year
   */
  async trackVdpVisit(vehicleData) {
    if (!this.isInitialized || !this.promotionAnalytics) {
      console.warn('VdpPromotionTracker not initialized');
      return;
    }

    this.promotionAnalytics.trackPromotionVdpVisit(vehicleData);
  }

  /**
   * Auto-track VDP visit by extracting vehicle data from page elements
   * @param {Object} selectors - CSS selectors for vehicle data elements
   */
  async autoTrackVdpVisit(selectors = {}) {
    if (!this.isInitialized || !this.promotionAnalytics) {
      console.warn('VdpPromotionTracker not initialized');
      return;
    }

    const defaultSelectors = {
      stockId: '[data-ga-stockid]',
      make: '[data-ga-make]',
      model: '[data-ga-model]',
      year: '[data-ga-year]',
      submodel: '[data-ga-submodel]',
    };

    const finalSelectors = { ...defaultSelectors, ...selectors };

    const vehicleData = {
      stock_id: this.extractTextContent(finalSelectors.stockId),
      make: this.extractTextContent(finalSelectors.make),
      model: this.extractTextContent(finalSelectors.model),
      year: this.extractTextContent(finalSelectors.year),
      submodel: this.extractTextContent(finalSelectors.submodel),
    };

    if (vehicleData.stock_id) {
      await this.trackVdpVisit(vehicleData);
    } else {
      console.debug(
        'No vehicle stock ID found, skipping VDP promotion tracking',
      );
    }
  }

  /**
   * Extract text content from an element using CSS selector
   * @param {string} selector - CSS selector
   * @returns {string|null}
   * @private
   */
  extractTextContent(selector) {
    const element = document.querySelector(selector);
    if (element) {
      return (
        element.textContent?.trim()
        || element.value?.trim()
        || element.dataset.value
        || null
      );
    }
    return null;
  }

  /**
   * Get the promotion analytics instance
   * @returns {PromotionAnalytics}
   */
  getPromotionAnalytics() {
    return this.promotionAnalytics;
  }
}

/**
 * Convenience function to quickly set up VDP promotion tracking
 * @param {Object} vehicleData - Vehicle data (optional, will auto-extract if not provided)
 * @param {Object} selectors - Custom selectors for auto-extraction (optional)
 * @returns {Promise<VdpPromotionTracker>}
 */
export async function initializeVdpPromotionTracking(
  vehicleData = null,
  selectors = {},
) {
  const tracker = new VdpPromotionTracker();
  await tracker.initialize();

  if (vehicleData) {
    await tracker.trackVdpVisit(vehicleData);
  } else {
    await tracker.autoTrackVdpVisit(selectors);
  }

  return tracker;
}
